import { IPassword, IUser } from '../../model/user/IUser';
import { requests } from '../../api/services';
import { multipartFormData } from '../../api/api.service';
import { AUTH_ME, LIST_AGENCY_URL, LIST_USERS_URL, SOCIAL_COMMAND_URL, SUPERVISOR_URL } from '../../utils/Endpoints';
import { BaseModel } from '../../model/shared/BaseModel';
import { ISocialCommand, PlatformItem } from '../../model/platform/PlatformItem';
import { UserPlatformsLogs } from '../../model/user/UserPlatformsLogs';
import { UserDevices } from '../../model/user/UserDevices';
import { IAdvancedSocialPlatform } from '../../model/user/IAdvancedSocialPlatform';
import { IAdvancedInvoiceData } from '../../model/user/IAdvancedInvoiceData';
import { IAdvancedDelivery, IAdvancedDeliveryAddress } from '../../model/user/IAdvancedDelivery';

export const UserService = {
    getPersonalInfo: (body: any): Promise<IUser> => requests.post(AUTH_ME, body),
    getUsers: (params?: any): Promise<BaseModel<IUser>> => requests.get(`${LIST_USERS_URL}`, params),
    getInfluencers: (params?: any): Promise<BaseModel<IUser>> =>
        requests.get(`${LIST_USERS_URL}/only/influencers`, params),
    getInfluencersWithFee: (params?: any) => requests.get(`${LIST_USERS_URL}/fee/influencers`, params),
    disableUser: (id: number): Promise<IUser> => requests.put(`${LIST_USERS_URL}/${id}/disable`, {}),
    editUser: (id: number, user: Partial<IUser>): Promise<IUser> => requests.put(`${LIST_USERS_URL}/${id}`, user),
    editUserAdvanced: (id: number, user: Partial<IUser>): Promise<IUser> =>
        requests.put(`${LIST_USERS_URL}/advanced/${id}`, user),
    getAdvancedInvoice: (id: number): Promise<IAdvancedInvoiceData> =>
        requests.get(`${LIST_USERS_URL}/advanced/${id}/invoice`),
    saveAdvancedInvoice: (
        id: number,
        data: Pick<IAdvancedInvoiceData, 'contact' | 'bank'>
    ): Promise<IAdvancedInvoiceData> => requests.put(`${LIST_USERS_URL}/advanced/${id}/invoice`, data),
    getAdvancedDelivery: (id: number): Promise<IAdvancedDelivery> =>
        requests.get(`${LIST_USERS_URL}/advanced/${id}/delivery`),
    saveAdvancedDelivery: (
        id: number,
        data: IAdvancedDeliveryAddress & { useOwnAddress: boolean }
    ): Promise<IAdvancedDelivery> => requests.put(`${LIST_USERS_URL}/advanced/${id}/delivery`, data),
    getAdvancedSocial: (id: number): Promise<IAdvancedSocialPlatform[]> =>
        requests.get(`${LIST_USERS_URL}/advanced/${id}/social`),
    getAdvancedAudience: (id: number, platformId: number): Promise<any> =>
        requests.get(`${LIST_USERS_URL}/advanced/${id}/social/${platformId}/audience`),
    saveManualAdvancedSocial: (id: number, platformId: number, file: File, body: object, isUpdate: boolean) =>
        multipartFormData(
            file,
            `${LIST_USERS_URL}/advanced/${id}/social/${platformId}/manual`,
            JSON.stringify(body),
            isUpdate ? 'put' : 'post'
        ).then(res => res.data),
    connectAdvancedSocial: (id: number, platformId: number, username: string) =>
        requests.post(`${LIST_USERS_URL}/advanced/${id}/social/${platformId}/connect`, { username }),
    refreshAdvancedSocial: (id: number, platformId: number) =>
        requests.post(`${LIST_USERS_URL}/advanced/${id}/social/${platformId}/refresh`, {}),
    toggleAdvancedSocial: (id: number, platformId: number) =>
        requests.put(`${LIST_USERS_URL}/advanced/${id}/social/${platformId}/toggle`, {}),
    disconnectAdvancedSocial: (id: number, platformId: number) =>
        requests.delete(`${LIST_USERS_URL}/advanced/${id}/social/${platformId}`),
    getAdvancedCategories: (id: number): Promise<{ id: number; name: string; isSelected: boolean }[]> =>
        requests.get(`${LIST_USERS_URL}/advanced/${id}/categories`),
    getUser: (id: number): Promise<IUser> => requests.get(`${LIST_USERS_URL}/${id}`),
    getUserPlatforms: (id: number): Promise<BaseModel<PlatformItem>> =>
        requests.get(`${LIST_USERS_URL}/platforms/${id}`),
    changeHandleUserPlatforms: (
        id: number,
        data: {
            title: string;
        }
    ) => requests.put(`${LIST_USERS_URL}/user-platform/${id}`, data),
    disablePlatform: (id: number) => requests.put(`${LIST_USERS_URL}/platforms/toggle/${id}`, {}),
    deletePlatform: (id: number) => requests.delete(`${LIST_USERS_URL}/platforms/delete/${id}`),
    getAgencies: (params?: any): Promise<BaseModel<IUser>> => requests.get(LIST_AGENCY_URL, params),
    changePassword: (id: number, data: IPassword) => requests.put(`${LIST_USERS_URL}/password/${id}`, data),
    postReauthorize: (data: ISocialCommand) =>
        requests.post(
            `${SOCIAL_COMMAND_URL}/${data.skip}/${data.take}/${data.userId}/${data.platformId}/${data.followers}/${data.content}`
        ),
    getUserPlatformLogs: (params?: any): Promise<BaseModel<UserPlatformsLogs>> =>
        requests.get(`${LIST_USERS_URL}/log/platforms`, params),
    suspendUser: (id: number): Promise<IUser> => requests.put(`${LIST_USERS_URL}/suspend/toggle/${id}`, {}),
    onboardUser: (id: number): Promise<IUser> => requests.put(`${LIST_USERS_URL}/is/onboarded/toggle/${id}`, {}),
    updateSupervisor: (id: number): Promise<IUser> => requests.put(`${SUPERVISOR_URL}/toggle/${id}`, {}),
    getUserDevices: (id: number | undefined): Promise<UserDevices[]> => requests.get(`${LIST_USERS_URL}/devices/${id}`),
    deleteUserDevices: (id: number | undefined): Promise<UserDevices[]> =>
        requests.delete(`${LIST_USERS_URL}/devices/${id}`),
};
